import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ADDITIVE_QR_ROLES, setTimezone } from "@/lib/helpers";

import { devLog } from "@/lib/devLog";
const AuthContext = createContext(null);

/**
 * QR access (`display_qr`, `scan_qr`) is a permission capability, not a
 * primary role. Whenever we need to determine which role to *display* or
 * default to, we must skip these additive keys so a user whose first
 * stored role happens to be `scan_qr` still reads as their real primary
 * role (Therapist, Reception, etc.) in the UI.
 */
function _pickPrimaryRole(rolesArr) {
  const list = rolesArr || [];
  const primary = list.find(r => !ADDITIVE_QR_ROLES.includes(r));
  return primary || list[0] || null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null | false | user
  const [org, setOrg] = useState(null);    // { id, name, slug } — active workspace

  /**
   * Update the active workspace and fire a "You are now viewing X" toast
   * whenever the org changes between sessions. Session-scoped so we
   * don't nag on every page refresh — only once per tab-lifetime.
   */
  const applyOrgWithToast = useCallback((nextOrg) => {
    if (!nextOrg) { setOrg(null); return; }
    setOrg(nextOrg);
    try {
      const prevOrgId = sessionStorage.getItem("tf_last_seen_org_id");
      if (prevOrgId && prevOrgId !== nextOrg.id) {
        toast.success(`You are now viewing ${nextOrg.name}`, {
          id: "tf-workspace-switch",
          description: "Workspace context updated — every list and search on this session is scoped to this workplace.",
          duration: 4500,
        });
      }
      sessionStorage.setItem("tf_last_seen_org_id", nextOrg.id);
    } catch (err) { devLog("workspace toast failed", err); }
  }, []);
  const [branches, setBranches] = useState([]);

  // Workspace wall clock. The head-office branch's `timezone` wins; when
  // no branch is flagged head office the first branch that declares one
  // applies — mirroring how the backend resolves it. Every date/time
  // helper renders against this, so reports, attendance, check-in/out and
  // session times all agree regardless of the viewer's device.
  useEffect(() => {
    const ordered = [...branches].sort((a, b) => (b.is_head_office ? 1 : 0) - (a.is_head_office ? 1 : 0));
    const tz = (ordered.find(b => b.timezone) || {}).timezone;
    if (tz) setTimezone(tz);
  }, [branches]);
  const [activeRole, setActiveRoleState] = useState(
    localStorage.getItem("tf_active_role") || null
  );
  const [activeBranch, setActiveBranchState] = useState(
    localStorage.getItem("tf_active_branch") || null
  );

  const bootstrap = useCallback(async () => {
    // CRITICAL — Google OAuth race guard.
    // When the browser lands on `/auth/callback[#session_id=…]`, the
    // <AuthCallback> child mounts and clears the hash before this
    // parent effect runs. If we relied on the hash we'd race the
    // callback's own /api/auth/google/session request: bootstrap's
    // /auth/me can 401 before the cookie is set and clobber the
    // user AuthCallback just wrote. So skip on PATH, deterministically.
    if (typeof window !== "undefined") {
      const p = window.location.pathname || "";
      if (p === "/auth/callback" || p.startsWith("/auth/callback/")
          || window.location.hash?.includes("session_id=")) {
        return;
      }
    }
    // The httpOnly access_token cookie tells the backend who we are. If
    // it's missing / expired, `/auth/me` returns 401 and we treat the
    // caller as anonymous. No secret is ever read from JS-visible storage.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      applyOrgWithToast(data.org);
      // initial defaults — coerce any stale QR-only activeRole in localStorage
      // to a proper primary role so profile / dashboard never reads
      // "display_qr view".
      if (data.user.roles?.length) {
        if (!activeRole || ADDITIVE_QR_ROLES.includes(activeRole)) {
          const r = _pickPrimaryRole(data.user.roles);
          if (r) { setActiveRoleState(r); localStorage.setItem("tf_active_role", r); }
        }
      }
      if (!activeBranch) {
        const b = (data.user.roles?.includes("owner")) ? "all" : (data.user.branch_ids?.[0] || null);
        if (b) {
          setActiveBranchState(b);
          localStorage.setItem("tf_active_branch", b);
        }
      }
      // Fetch branches
      try {
        const br = await api.get("/branches");
        setBranches(br.data || []);
      } catch (err) { devLog("branches fetch failed", err); }
    } catch (_e) {
      setUser(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password, orgSlug = null) => {
    const { data } = await api.post("/auth/login", { email, password, ...(orgSlug ? { org_slug: orgSlug } : {}) });
    // Multi-workspace user with no slug picked yet — bubble the workspace
    // list up to the caller (LoginPage renders a picker card).
    if (data?.needs_workspace_selection) return data;
    // JWT lives in the httpOnly `access_token` cookie set by the backend
    // response — never touched here. Only UI-preference values go into
    // localStorage.
    if (orgSlug) localStorage.setItem("tf_org_slug", orgSlug);
    setUser(data.user);
    applyOrgWithToast(data.org);
    const firstRole = _pickPrimaryRole(data.user.roles);
    if (firstRole) { setActiveRoleState(firstRole); localStorage.setItem("tf_active_role", firstRole); }
    const firstBranch = data.user.roles?.includes("owner") ? "all" : (data.user.branch_ids?.[0] || null);
    if (firstBranch) { setActiveBranchState(firstBranch); localStorage.setItem("tf_active_branch", firstBranch); }
    try {
      const br = await api.get("/branches");
      setBranches(br.data || []);
    } catch (err) { devLog("branches fetch failed", err); }
    return data.user;
  };

  const superLogin = async (email, password, orgSlug = null) => {
    const { data } = await api.post("/auth/super-login", { email, password, ...(orgSlug ? { org_slug: orgSlug } : {}) });
    if (data?.needs_workspace_selection) return data;
    if (orgSlug) localStorage.setItem("tf_org_slug", orgSlug);
    setUser(data.user);
    applyOrgWithToast(data.org);
    const firstRoleSA = _pickPrimaryRole(data.user.roles);
    if (firstRoleSA) { setActiveRoleState(firstRoleSA); localStorage.setItem("tf_active_role", firstRoleSA); }
    const firstBranch = data.user.roles?.includes("owner") ? "all" : (data.user.branch_ids?.[0] || null);
    if (firstBranch) { setActiveBranchState(firstBranch); localStorage.setItem("tf_active_branch", firstBranch); }
    try {
      const br = await api.get("/branches");
      setBranches(br.data || []);
    } catch (err) { devLog("branches fetch failed", err); }
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("tf_org_slug", data.org.slug);
    setUser(data.user);
    applyOrgWithToast(data.org);
    const firstRole = _pickPrimaryRole(data.user.roles) || "owner";
    setActiveRoleState(firstRole);
    localStorage.setItem("tf_active_role", firstRole);
    setActiveBranchState("all");
    localStorage.setItem("tf_active_branch", "all");
    return data;
  };

  const logout = async () => {
    // Backend `/auth/logout` clears the httpOnly cookie. UI-preference
    // localStorage keys are cleared here for a clean next-login flow.
    try { await api.post("/auth/logout"); } catch (err) { devLog("logout call failed", err); }
    localStorage.removeItem("tf_active_role");
    localStorage.removeItem("tf_active_branch");
    localStorage.removeItem("tf_org_slug");
    sessionStorage.removeItem("tf_last_seen_org_id");
    setUser(false);
    setOrg(null);
    setActiveRoleState(null);
    setActiveBranchState(null);
  };

  const setActiveRole = async (role) => {
    if (!user?.roles?.includes(role)) { toast.error("You don't have this role"); return; }
    if (ADDITIVE_QR_ROLES.includes(role)) {
      // QR access is a capability, not a switchable primary role. Refuse
      // silently — the role selector already filters these out, but this
      // guards against direct callers.
      return;
    }
    try {
      await api.post("/auth/switch", { active_role: role });
      setActiveRoleState(role);
      localStorage.setItem("tf_active_role", role);
      toast.success(`Switched to ${role} view`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setActiveBranch = async (branchId) => {
    try {
      await api.post("/auth/switch", { active_branch_id: branchId });
      setActiveBranchState(branchId);
      localStorage.setItem("tf_active_branch", branchId);
      const label = branchId === "all" ? "All branches" : (branches.find((b) => b.id === branchId)?.name || "Branch");
      toast.success(`Now viewing ${label}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setAuthedFromGoogle = async (u, org = null) => {
    setUser(u);
    if (org) applyOrgWithToast(org);
    const firstRole = _pickPrimaryRole(u.roles);
    if (firstRole) { setActiveRoleState(firstRole); localStorage.setItem("tf_active_role", firstRole); }
    const firstBranch = u.roles?.includes("owner") ? "all" : (u.branch_ids?.[0] || null);
    if (firstBranch) { setActiveBranchState(firstBranch); localStorage.setItem("tf_active_branch", firstBranch); }
    try {
      const br = await api.get("/branches");
      setBranches(br.data || []);
    } catch (err) { devLog("branches fetch failed", err); }
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      applyOrgWithToast(data.org);
      return data.user;
    } catch (err) { devLog("refresh me failed", err); }
  };

  const refreshBranches = useCallback(async () => {
    try {
      const br = await api.get("/branches");
      setBranches(br.data || []);
    } catch (err) { devLog("branches refresh failed", err); }
  }, []);

  // Memoise the context value so consumers don't re-render on unrelated
  // parent updates. Only re-created when the referenced state actually
  // changes (code-review: iter31, contexts should never pass fresh
  // object literals on every render).
  const contextValue = useMemo(() => ({
    user, org, branches, activeRole, activeBranch,
    login, superLogin, signup, logout, setActiveRole, setActiveBranch, setAuthedFromGoogle,
    refreshUser, refreshBranches,
  }), [user, org, branches, activeRole, activeBranch, refreshBranches]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
